import { useEffect, useState } from 'react'
import { api, type Portfolio } from '../api'
import { useDesks } from '../desks'

/**
 * Choosing a fund.
 *
 * Lookup and Compare both used to open on an empty search box. That is the
 * right control for a firm with four hundred funds and the wrong one for
 * everybody, because it asks the reader to already know the answer: an empty box
 * over an appliance holding nine portfolios tells them nothing about which nine,
 * and *code or name* as placeholder text is a hint about the query language
 * rather than about the data.
 *
 * A list they can open answers *which funds are here* — which `/api/portfolios`
 * has always called "the first question on the lookup screen" — before asking
 * them to pick one from it. The list is reader-open for that reason and needs no
 * new permission.
 *
 * The count of limits comes with each entry, because on a screen whose whole
 * job is showing what binds a fund, *0 limits* is worth knowing before the click
 * rather than after.
 */
export function useFunds() {
  const [funds, setFunds] = useState<Portfolio[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    api
      .portfolios()
      .then((all) => {
        if (!live) return
        setFunds([...all].sort((a, b) => a.code.localeCompare(b.code)))
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => live && setLoaded(true))
    return () => {
      live = false
    }
  }, [])

  return { funds, error, loaded }
}

export function FundPicker({
  label,
  value,
  onChange,
  funds,
  placeholder = 'Choose a fund',
  exclude,
  testId,
}: {
  label: string
  value: string
  onChange: (id: string) => void
  funds: Portfolio[]
  placeholder?: string
  /** A fund already chosen in the other picker. Comparing a fund with itself is
   *  a screen with nothing on it, so it is not offered rather than allowed and
   *  then explained. */
  exclude?: string
  testId?: string
}) {
  // Somebody across the whole firm chooses the desk first, and then among its funds
  // (decision 91). Everybody else sees one desk's funds already, and no desk control.
  const { firmWide, desks } = useDesks()
  const chosen = funds.find((f) => f.id === value)
  const [desk, setDesk] = useState(chosen?.desk_code ?? '')
  useEffect(() => {
    if (chosen?.desk_code) setDesk(chosen.desk_code)
  }, [chosen?.desk_code])
  const offered = funds.filter((f) => f.id !== exclude && (!firmWide || !desk || f.desk_code === desk))

  return (
    <>
      {firmWide && desks.length > 0 && (
        <label>
          Desk
          <select
            value={desk}
            data-testid={testId ? `${testId}-desk` : undefined}
            onChange={(e) => {
              setDesk(e.target.value)
              // A fund on another desk is no longer a choice.
              if (chosen && e.target.value && chosen.desk_code !== e.target.value) onChange('')
            }}
          >
            <option value="">Every desk</option>
            {desks.map((d) => (
              <option key={d.id} value={d.code} title={d.name}>
                {d.code} · {d.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        {label}
        <select
          value={value}
          data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {offered.map((f) => (
            <option key={f.id} value={f.id}>
              {/* The name only where it says something the code does not: a fund
                  imported from a workbook column has its code for a name, and
                  "HALVEN · HALVEN" said one thing twice. */}
              {f.name && f.name !== f.code ? `${f.code} · ${f.name}` : f.code} ({f.rule_count}{' '}
              limit{f.rule_count === 1 ? '' : 's'})
            </option>
          ))}
        </select>
      </label>
    </>
  )
}
